/**
 * ApprovalNumberField — numeric answer input for a Maestro `user_input` gate
 *
 * Purpose: When Maestro asks for a number ("What is the total budget for this
 *          campaign?") the answer must look and behave exactly like the same
 *          field on the Create Media Plan form: thousands separators as the user
 *          types, the plan's currency symbol, and a plain number underneath
 *          (ORCH-374 Biz Review — the card used to render its own bare
 *          `<input type="number">`, which browsers cannot group at all).
 * Architecture: Renders the shared `CurrencyFieldRenderer` — the renderer every
 *               currency / decimal / number field goes through — with the plan
 *               form's *own* Field row for `fd_key`, read from the same
 *               `mediaplan` edit-modal view the form renders (ORCH-381 cache).
 *               Precision, label and datatype therefore come from metadata, not
 *               from this component. The currency symbol follows the form's
 *               Currency field the same way `CreateMediaPlanTemplate` resolves
 *               it (ORCH-377): the selected `fd_currency` option's `meta_data.fd_code`.
 *               A number field the view does not describe falls back to a
 *               plain `number` field shaped from the gate's payload, so the
 *               mask still applies.
 * Dependencies: metadata-form/FieldRenderers (CurrencyFieldRenderer),
 *               template-form/PlanningCurrencyContext, hooks/useModelFieldLabels,
 *               services/mdmCacheService
 */

import React, { useMemo } from 'react';
import { CurrencyFieldRenderer } from '../metadata-form/FieldRenderers';
import type { ViewField } from '../metadata-form/types';
import { PlanningCurrencyProvider } from '../template-form/PlanningCurrencyContext';
import { useCreatePlanFieldLabels } from '../../hooks/useModelFieldLabels';
import { useMDMValues } from '../../services/mdmCacheService';

/** The Create Media Plan field whose selected option carries the ISO currency code. */
export const PLAN_CURRENCY_FD_KEY = 'fd_currency';

/**
 * fallbackNumberViewField — a `number` Field row for a key the plan view does not describe
 *
 * Purpose: Keeps the shared renderer (and its mask) in play for a numeric question
 *          about a field that is hidden from, or not on, the Create Media Plan view.
 * Input: fdKey — the gate's `fd_key`; label — the gate's human field name
 * Output: ViewField with the renderer's default precision (storage 5 / display 2)
 */
export const fallbackNumberViewField = (fdKey: string, label: string): ViewField => ({
  field_id: `approval-${fdKey}`,
  fielddef_id: fdKey,
  tenant_id: '',
  model_id: '',
  storage_type: 'metadata',
  display_order: 0,
  field_definition: {
    fielddef_id: fdKey,
    label,
    datatype: 'number',
    is_multiselect: false,
  },
});

/**
 * resolveCurrencyCode — ISO code of the plan form's selected Currency option
 *
 * Purpose: Same lookup `CreateMediaPlanTemplate.resolvePlanningCurrency` performs
 *          (selected `fd_currency` option → `meta_data.fd_code`), done here
 *          because the approval card lives outside the form's provider.
 * Input: selected — the form's `fd_currency` value (option id, or an array of one)
 *        options — MDM values for `fd_currency`
 * Output: the ISO 4217 code, or null when nothing is selected / cached yet
 */
export const resolveCurrencyCode = (
  selected: unknown,
  options: readonly { id: string; meta_data?: Record<string, unknown> }[]
): string | null => {
  const scalar = Array.isArray(selected) ? selected[0] : selected;
  if (scalar === undefined || scalar === null || scalar === '') return null;
  const option = options.find((o) => String(o.id) === String(scalar));
  const code = option?.meta_data?.fd_code;
  return typeof code === 'string' && code ? code : null;
};

export interface ApprovalNumberFieldProps {
  tenantId: string;
  /** The plan field this question answers (e.g. `fd_budget`). */
  fdKey: string;
  /** The gate's human field name — label fallback while the view loads. */
  fallbackLabel: string;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  /** The plan form's current Currency value, so the symbol matches the form. */
  currencyFieldValue?: unknown;
  /** Forwarded to the input element (test ids, aria). */
  inputProps?: Record<string, unknown>;
}

const ApprovalNumberField: React.FC<ApprovalNumberFieldProps> = ({
  tenantId,
  fdKey,
  fallbackLabel,
  value,
  onChange,
  disabled,
  currencyFieldValue,
  inputProps,
}) => {
  const { fieldFor, labelFor } = useCreatePlanFieldLabels(tenantId);
  const { data: currencyOptions = [] } = useMDMValues(PLAN_CURRENCY_FD_KEY, tenantId, {}, {});

  const field = useMemo(
    () => fieldFor(fdKey) ?? fallbackNumberViewField(fdKey, labelFor(fdKey, fallbackLabel)),
    [fieldFor, labelFor, fdKey, fallbackLabel]
  );

  const currencyCode = useMemo(
    () => resolveCurrencyCode(currencyFieldValue, currencyOptions),
    [currencyFieldValue, currencyOptions]
  );

  return (
    <PlanningCurrencyProvider value={currencyCode}>
      <CurrencyFieldRenderer
        field={field}
        value={value}
        onChange={(next) => onChange(typeof next === 'number' ? next : null)}
        disabled={disabled}
        inputProps={{ 'data-testid': `approval-number-input-${fdKey}`, ...(inputProps || {}) }}
      />
    </PlanningCurrencyProvider>
  );
};

export default ApprovalNumberField;
